import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 0h-3.2v15.9a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .8.1V9.8a6.2 6.2 0 1 0 5.3 6.1V8.1a7.3 7.3 0 0 0 4.3 1.4V6.3a4.3 4.3 0 0 1-4.3-4.3V0z" />
  </svg>
);
import { imgUrl } from '../utils/imgUrl';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <img
                src={imgUrl("https://customer-assets.emergentagent.com/job_2b9a73bd-ff0b-417b-bbf4-234b110c9b5c/artifacts/j7xyz0vb_ChatGPT%20Image%20Jul%205%2C%202026%2C%2012_45_33%20AM.png")}
                alt="Rezvani Furniture Studio"
                className="h-44 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Premium in-pool furniture designed for luxury and comfort. Transform your pool into a resort-style paradise.
            </p>
            <div className="flex space-x-3">
              <a href="https://www.facebook.com/p/Rezvani-Furniture-61591016702869/" target="_blank" rel="noopener noreferrer" aria-label="Rezvani Furniture Studio on Facebook" className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-cyan-600 flex items-center justify-center transition-colors duration-300" data-testid="social-facebook">
                <Facebook size={18} />
              </a>
              <a href="https://www.instagram.com/rezvanifurniturestudio/" target="_blank" rel="noopener noreferrer" aria-label="Rezvani Furniture Studio on Instagram" className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-cyan-600 flex items-center justify-center transition-colors duration-300" data-testid="social-instagram">
                <Instagram size={18} />
              </a>
              <a href="https://www.tiktok.com/@rezvanifurnitures" target="_blank" rel="noopener noreferrer" aria-label="Rezvani Furniture Studio on TikTok" className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-cyan-600 flex items-center justify-center transition-colors duration-300" data-testid="social-tiktok">
                <TikTokIcon size={18} />
              </a>
              <a href="https://www.youtube.com/@RezvaniFurnitureStudio" target="_blank" rel="noopener noreferrer" aria-label="Rezvani Furniture Studio on YouTube" className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-cyan-600 flex items-center justify-center transition-colors duration-300" data-testid="social-youtube">
                <Youtube size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-cyan-400 transition-colors duration-300">Home</Link>
              </li>
              <li>
                <Link to="/products" className="hover:text-cyan-400 transition-colors duration-300">Products</Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-cyan-400 transition-colors duration-300">About Us</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-cyan-400 transition-colors duration-300">Contact</Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-cyan-400 transition-colors duration-300">FAQ</Link>
              </li>
            </ul>
          </div>

          {/* Product Categories */}
          <div>
            <h3 className="text-white font-semibold mb-4">Categories</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/products?category=lounge-chairs" className="hover:text-cyan-400 transition-colors duration-300">Lounge Chairs</Link>
              </li>
              <li>
                <Link to="/products?category=tables" className="hover:text-cyan-400 transition-colors duration-300">Pool Tables</Link>
              </li>
              <li>
                <Link to="/products?category=stools" className="hover:text-cyan-400 transition-colors duration-300">Pool Stools</Link>
              </li>
              <li>
                <Link to="/products?category=benches" className="hover:text-cyan-400 transition-colors duration-300">Pool Benches</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start space-x-2">
                <MapPin size={18} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                <span>Chandler, AZ</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone size={18} className="text-cyan-500 flex-shrink-0" />
                <span>(623) 888-8644</span>
              </li>
              <li className="flex items-center space-x-2">
                <Mail size={18} className="text-cyan-500 flex-shrink-0" />
                <span>rezvanifurniturestudio@gmail.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <p className="text-xs text-gray-500 mb-4 max-w-4xl" data-testid="footer-authorized-retailer">
            Rezvani Furniture Studio is an authorized online retailer of Tenjam&reg; SPLASH Series in-pool furniture. Tenjam&reg; and the SPLASH Series are trademarks of their respective owner; all product designs and patents belong to the manufacturer. Rezvani Furniture Studio is an independent, U.S.-based retail business located in Chandler, Arizona, and is not the manufacturer.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Rezvani Furniture Studio. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/about#privacy" className="hover:text-cyan-400 transition-colors duration-300">Privacy Policy</Link>
              <Link to="/about#terms" className="hover:text-cyan-400 transition-colors duration-300">Terms of Service</Link>
              <Link to="/about#terms" className="hover:text-cyan-400 transition-colors duration-300">Shipping Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
